import { Gender } from "@/types/enums/Gender";
import { Guest } from "@/types/Guest";
import { Page } from "@/types/Page";
import { parseTriStateBoolean } from "./parser";
import { normalizeFieldValue } from "./input-validator";
import { Event } from "../../types/Event";
import { toInstantMs } from "./event-date-bounds";

/**
 * Sanitizes an Guest object by converting empty strings to undefined
 * and ensuring proper type conversion for enums and booleans.
 *
 * @param guest The Guest object to sanitize
 * @returns A new sanitized Guest object
 */
export function sanitizeGuest(guest: Guest): Guest {
  // Create a copy of the guest
  const sanitized = { ...guest };

  // Edge-whitespace normalization, FIRST — before the ""→undefined pass below,
  // so a whitespace-only entry collapses to "" and is dropped like any other
  // empty field instead of persisting as " ".
  //
  // This is the create dialog's ONLY normalization point: `PCreateGuest` hands
  // its form object to `addGuest` → `performCreate` → `createGuests`, which
  // never touches `parseGuest`/`parseString`. So the seam that covers inline
  // cell edits and imports does not reach this door — and a value stored with
  // an edge space is unreachable by the `=` filter forever, because the read
  // side trims its operand (`parseFiltersApiRequest`) while `=` is an exact
  // Mongo `$eq`.
  //
  // Scope, precisely: this covers the CREATE paths only. The update lane
  // (`updateGuests`, the bulk-edit dispatch) and the four server-side mint
  // paths (CRM→guest, speaker promotion, event duplication, AI tools) do not
  // pass through here and carry their own normalization.
  //
  // `username`/`code` are excluded: they are login identity behind the partial
  // UNIQUE index (eventId, username), and the backend carve-out in
  // `GuestService.bulkUpdateByFilter` skips them for the same reason.
  const IDENTITY_KEYS = new Set(["username", "code"]);
  for (const [key, value] of Object.entries(sanitized)) {
    if (key === "additionalFields" || IDENTITY_KEYS.has(key)) continue;
    if (typeof value === "string") {
      (sanitized as Record<string, unknown>)[key] = normalizeFieldValue(value);
    }
  }
  // A NEW map, never an in-place rewrite: `{ ...guest }` is shallow, so
  // `sanitized.additionalFields` is still the caller's object — mutating it
  // would edit the live form state behind the dialog's back. (The ""-drop pass
  // below builds a fresh object for exactly this reason.)
  if (sanitized.additionalFields) {
    const normalizedAf: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(sanitized.additionalFields)) {
      normalizedAf[key] =
        typeof value === "string"
          ? normalizeFieldValue(value)
          : Array.isArray(value)
            ? // Blank elements are DROPPED, not normalized into place. A
              // MULTI_SELECT value is a set of chosen options and a blank is not
              // a choice — and mapping over the elements is precisely what turns
              // `[" "]` into `[""]`, one of the two shapes the blank contract
              // declares unwritable (main-backend/docs/whitespace-wave-decision.md §3). Mongo's
              // emptiness clause matches an array ELEMENT-WISE, so a stored
              // `["VIP",""]` reads as blank and the guest who picked VIP leaves
              // every "has a value" audience.
              //
              // Every backend door now REJECTS such an element, so without this
              // the drawer would have started 400-ing on a value it had happily
              // sent the day before. Same rule on both sides of the wire.
              value
                .map((el) =>
                  typeof el === "string" ? normalizeFieldValue(el) : el,
                )
                .filter((el) => el !== "")
            : value;
    }
    sanitized.additionalFields = normalizedAf;
  }

  // `mainGuestDTO` is a display-only lookup (the resolved main guest, used to
  // render the Hauptgast/Verknüpfung name without a refetch). It must never be
  // sent to the API — it carries nested guest data and isn't an input field.
  // The companion picker now populates it for the optimistic row, so strip it
  // here at the send-side chokepoint every CREATE path funnels through
  // (createGuests / createCompanionRequest). Update paths do not pass here.
  if ("mainGuestDTO" in sanitized) delete sanitized.mainGuestDTO;

  // Check-in state must never ride a create payload: checkedIn is tri-state
  // (true = in, false = checked OUT, null/absent = never scanned) and the
  // backend resets it on create — strip it here too so a spread-in fetched
  // guest can never reintroduce the false-on-create bug from the FE side.
  if ("checkedIn" in sanitized) delete sanitized.checkedIn;
  if ("checkedInAt" in sanitized) delete sanitized.checkedInAt;

  // Convert empty strings to undefined using a type-safe approach
  if (sanitized.title === "") sanitized.title = undefined;
  if (sanitized.mainGuest === "") sanitized.mainGuest = undefined;
  if (sanitized?.firstName === "") sanitized.firstName = undefined;
  if (sanitized.lastName === "") sanitized.lastName = undefined;
  if (sanitized.company === "") sanitized.company = undefined;
  if (sanitized.jobTitle === "") sanitized.jobTitle = undefined;
  if (sanitized.address === "") sanitized.address = undefined;
  if (sanitized.address2 === "") sanitized.address2 = undefined;
  if (sanitized.zipCode === "") sanitized.zipCode = undefined;
  if (sanitized.city === "") sanitized.city = undefined;
  if (sanitized.country === "") sanitized.country = undefined;
  if (sanitized.email === "") sanitized.email = undefined;
  if (sanitized.phone === "") sanitized.phone = undefined;
  if (sanitized.mobile === "") sanitized.mobile = undefined;
  if (sanitized.username === "") sanitized.username = undefined;
  if (sanitized.code === "") sanitized.code = undefined;

  // Special handling for gender enum
  if (sanitized.gender === ("" as unknown as Gender))
    sanitized.gender = undefined;

  // Tri-state emailOptIn: "true" → true, "false" → false, "" (the "—"
  // not-set option) → null. Sent to the backend as a real null.
  if (typeof sanitized.emailOptIn === "string") {
    sanitized.emailOptIn = parseTriStateBoolean(sanitized.emailOptIn);
  }
  // SMS channel wave: the SMS consent, same tri-state.
  if (typeof sanitized.smsOptIn === "string") {
    sanitized.smsOptIn = parseTriStateBoolean(sanitized.smsOptIn);
  }

  // Handle additionalFields - create a new object with non-empty values
  if (sanitized.additionalFields) {
    const cleanedAdditionalFields: Record<string, unknown> = {};
    let hasValues = false;

    for (const [key, value] of Object.entries(sanitized.additionalFields)) {
      if (value !== "") {
        cleanedAdditionalFields[key] = value;
        hasValues = true;
      }
    }

    // Only set additionalFields if there are actual values
    sanitized.additionalFields = hasValues
      ? cleanedAdditionalFields
      : undefined;
  }

  return sanitized;
}


/** Type-safe helper to assign a field from one Event partial to another */
function assignEventField<K extends keyof Event>(
  target: Partial<Event>,
  source: Event,
  field: K
): void {
  target[field] = source[field];
}

export const getChangedFieldsEvent = (
  original: Event,
  updated: Event
): Partial<Event> => {
  const changes: Partial<Event> = {};

  // Define all possible Event fields to check
  const fieldsToCheck: (keyof Event)[] = [
    "name",
    "start",
    "end",
    "timeZone",
    "address",
    "address2",
    "zipCode",
    "city",
    "country",
    "registrationType",
    "locationName",
    "archived",
    "additionalFields",
    "showVeltricBranding",
    "crmFeedEnabled",
  ];

  fieldsToCheck.forEach((field) => {
    const originalValue = original[field];
    const updatedValue = updated[field];

    // Dates compare by INSTANT, not by value identity. `start`/`end` arrive
    // from the API as ISO strings and become Date objects after the first
    // edit, so the old number-vs-string compare reported that type flip as a
    // change: re-picking the very same minute put an unmoved `start` into the
    // payload, where the backend's past-date bound rejected the whole PUT.
    // Shared with the form's own "did this date move?" gate so the two can
    // never disagree about what is being sent.
    if (field === "start" || field === "end") {
      if (
        toInstantMs(originalValue as Date | string | undefined) !==
        toInstantMs(updatedValue as Date | string | undefined)
      ) {
        assignEventField(changes, updated, field);
      }
    }
    // Handle additionalFields object specially
    else if (field === "additionalFields") {
      if (JSON.stringify(originalValue) !== JSON.stringify(updatedValue)) {
        assignEventField(changes, updated, field);
      }
    }
    // Handle primitive values and other objects
    else {
      if (originalValue !== updatedValue) {
        assignEventField(changes, updated, field);
      }
    }
  });

  return changes;
};

